import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { Link, useNavigate, useRouter } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { Fragment, useCallback, useEffect, useState } from "react"


import { Avatar } from "@/components/Avatar/Avatar.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { ButtonStyles } from "@/components/Button/ButtonStyles.ts"
import { Centered } from "@/components/Centered/Centered.ts"
import { ConfirmDialog } from "@/components/Dialog/Dialog.tsx"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { GitHubPrBadge } from "@/components/GitHubIntegration/GitHubPrBadge.tsx"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import MaybeSelected from "@/components/MaybeSelected/MaybeSelected.tsx"
import { Principal, ProfileLink } from "@/components/Principal.tsx"
import { ProjectSettingsDialog } from "@/components/ProjectSettingsDialog/ProjectSettingsDialog.tsx"
import { TabId } from "@/components/ProjectSettingsDialog/ProjectSettingsTabId.ts"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useNewTaskLocation, useOrgHomeLocation } from "@/hooks/useMatterLocation.ts"
import {
  ChevronDownRegular,
  CreateSemibold,
  DeleteLight,
  Heart12Gray,
  Heart12Green,
  Heart20Green,
  Icon16,
  Icon20,
  LinkLight,
  SettingsRegular,
  StarColor
} from "@/icons/Icons.ts"
import { formatTime } from "@/lib/date.ts"
import { formatTaskTitle, pageTitle } from "@/lib/displayUtils.ts"
import { startViewTransition } from "@/lib/dom.ts"
import { copyToClipboard, isUserPrincipal } from "@/lib/std.ts"
import { Page } from "@/routes/auth/Page.tsx"
import { PAGE_HORIZONTAL_PADDING, PAGE_HORIZONTAL_PADDING_MOBILE } from "@/routes/auth/PageStyles.ts"
import { Error } from "@/routes/errors/Error.tsx"
import { HomeStyles } from "@/routes/home/HomeStyles.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId } from "@/routes/projects/projectHooks.ts"
import { TaskListId } from "@/routes/tasks/TaskListScene/TaskListId.ts"
import { TaskViewSceneStyles } from "@/routes/tasks/TaskListScene/TaskListSceneStyles.ts"
import { useTasksRoute } from "@/routes/tasks/tasksHooks.ts"
import { HeadingStyles } from "@/styles/Markdown.ts"
import { ellipsis, mobileBreakpoint, Rotate90 } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { Characters, ellipsize } from "@/styles/textStyles.ts"

import type { AiAgentTask, CPrincipal, GitHubPrInfo, ProjectKey } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskListModel } from "@/routes/tasks/TaskListScene/TaskListModel.ts"

export const TaskListScene = observer(() => {
  const orgId = useCurrentOrgId()
  const navigate = useNavigate()
  const route = useTasksRoute()
  const model = route.useLoaderData().model

  useEffect(() => {
    return () => {
      model.terminate()
    }
  }, [model])

  const homeLocation = useOrgHomeLocation()

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false)

  const [scrollAtBottom, setScrollAtBottom] = useState(false)

  const checkIfAtBottom = useCallback(() => {
    const container = document.scrollingElement
    if (!container) return

    setScrollAtBottom(container.scrollTop + container.clientHeight >= container.scrollHeight)
  }, [])

  useEffect(() => {
    checkIfAtBottom()

    window.addEventListener("scroll", checkIfAtBottom)
    window.addEventListener("resize", checkIfAtBottom)

    return () => {
      window.removeEventListener("scroll", checkIfAtBottom)
      window.removeEventListener("resize", checkIfAtBottom)
    }
  }, [checkIfAtBottom])


  const project = model.project.get()

  if (!project) return null

  return (
    <Page>
      <title>{pageTitle("Ideas", project.name)}</title>

      <Centered vertical={false} gap="16px">
        <ProjectNameWrapper className={LimitedWidth}>
          <Tooltip content="Back to project list" side="right">
            <Button
              kind="icon"
              onClick={() => startViewTransition(() => navigate(homeLocation))}
              data-testid="back-button"
            >
              <ChevronDownRegular className={Rotate90} />
            </Button>
          </Tooltip>

          <ProjectNameContainer>
            <ProjectName data-testid="project-title">{project.name || Characters.Nbsp}</ProjectName>
            {model.isFavoriteProject.get() && <StarColor className={Icon20} />}
          </ProjectNameContainer>

          <Tooltip content="Project settings" side="left">
            <Link
              {...Navigator.projectSettings(orgId, project.key, TabId.General)}
              onClick={(e) => {
                e.preventDefault()
                setSettingsDialogOpen(true)
              }}
            >
              {(model.repoSyncStatus.get()?.authError === true)
                ? <ErrorColoredButton kind="icon"><SettingsRegular /></ErrorColoredButton>
                : <Button data-testid="settings-button" kind="icon" tabIndex={-1}><SettingsRegular /></Button>
              }
            </Link>
          </Tooltip>

          <ProjectSettingsDialog
            project={project}
            dialogOpen={settingsDialogOpen}
            setDialogOpen={setSettingsDialogOpen}
          />
        </ProjectNameWrapper>
        <ListTypeSwitch list={model.filter} successful={model.successful} />
        <TaskList model={model} withNewButton />
        {!scrollAtBottom && <FadeOutGradient />}
      </Centered>
    </Page>
  )
})

export const TaskList = observer((props: {
  model: TaskListModel
  withNewButton?: boolean
}) => {
  const model = props.model

  const project = model.project.get()
  const repo = model.projectRepository.get()

  const navigate = useNavigate()

  const handlePrev = () => {
    void navigate({ to: ".", search: { page: model.page - 1 } })
  }

  const handleNext = () => {
    void navigate({ to: ".", search: { page: model.page + 1 } })
  }

  const tasksByDate = model.tasksByDate.get()
  const newTaskLocation = useNewTaskLocation(project?.key)

  const error = model.error.get()

  if (!project || !repo) return null

  if (error) {
    return (
      <Centered>
        <Error error={error} />
      </Centered>
    )
  }

  if (model.isLoading.get()) {
    return (
      <Centered>
        <Spinner text={ellipsize("Loading ideas")} />
      </Centered>
    )
  }

  return (
    <TaskListContainer className={LimitedWidth}>
      {props.withNewButton && (
        <NewTaskButton
          data-testid="new-task-button"
          className={HeadingStyles.Heading2}
          {...newTaskLocation}
          viewTransition
        >
          <CreateSemibold className={Icon16} />
          New idea
        </NewTaskButton>
      )}

      {tasksByDate.map(({ date, tasks }) => (
        <Fragment key={date}>
          <HomeStyles.ListTitle className={HeadingStyles.Heading6}>{date}</HomeStyles.ListTitle>

          {tasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              projectKey={project.key}
              repo={repo}
              prs={model.gitHubPrs.get().get(task.id)}
              deleteItem={model.deleteTask}
            />
          ))}
        </Fragment>
      ))}

      {model.hasMore.get() && (
        <Horizontal key="paging">
          {model.hasPrev.get() && (
            <Button kind="alternative" onClick={handlePrev}>
              Previous
            </Button>
          )}

          <Spring />

          {model.hasNext.get() && (
            <Button kind="alternative" onClick={handleNext}>
              Next
            </Button>
          )}
        </Horizontal>
      )}
    </TaskListContainer>
  )
})

const ListTypeSwitch = (props: {
  list: TaskListId
  successful: boolean
}) => {
  return (
    <ListTypeSwitchContainer className={LimitedWidth}>
      <Link to="." search={{ list: TaskListId.All, successful: props.successful }}>
        <MaybeSelected selected={props.list == TaskListId.All}>All</MaybeSelected>
      </Link>

      <Link to="." search={{ list: TaskListId.My, successful: props.successful }}>
        <MaybeSelected selected={props.list == TaskListId.My}>My</MaybeSelected>
      </Link>

      <Link to="." search={{ list: TaskListId.Coop, successful: props.successful }}>
        <MaybeSelected selected={props.list == TaskListId.Coop}>Co-op</MaybeSelected>
      </Link>

      <Spring />

      <SuccessfulToggle
        to="."
        search={{ list: props.list, successful: !props.successful }}
        className={cx(ButtonStyles.Base, ButtonStyles.Kind.Alternative, props.successful ? SuccessfulActive : null)}
      >
        <Horizontal alignItems="center" gap="4px">
          {props.successful ? <Heart12Green /> : <Heart12Gray />}
          Successful
        </Horizontal>
      </SuccessfulToggle>
    </ListTypeSwitchContainer>
  )
}

const TaskItem = (props: {
  task: AiAgentTask
  projectKey: ProjectKey
  repo: string
  prs: GitHubPrInfo[] | undefined

  deleteItem: (taskId: string) => void
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [hovered, setHovered] = useState(false)

  const orgId = useCurrentOrgId()
  const navigate = useNavigate()
  const router = useRouter()

  const profile = getProfileFromPrincipal(props.task.createdBy)

  const task = Navigator.task(orgId, props.projectKey, props.task.id)

  const mostRecentPr = props.prs && props.prs.length > 0 ? props.prs[props.prs.length - 1] : null

  const CardComponent =
    mostRecentPr?.state?.toLowerCase() === "merged"
      ? props.task.successful
        ? TaskViewSceneStyles.MergedSuccessfulItemCard
        : TaskViewSceneStyles.MergedItemCard
      : props.task.successful || mostRecentPr?.state?.toLowerCase() === "open"
        ? TaskViewSceneStyles.SuccessfulItemCard
        : TaskViewSceneStyles.ItemCard

  return (
    <CardComponent
      onClick={() => navigate(task)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid="task-card"
    >
      <Horizontal alignItems="center" gap="8px" flex="1 1 auto">
        <Content alignItems="flex-start" gap="8px" flex="1 1 auto">
          <FirstLine>
            <EllipsisContainer>
              <ItemLink {...task} onClick={e => e.stopPropagation()} viewTransition data-testid="task-card-title">
                {formatTaskTitle(props.task.title) || <em>Untitled</em>}
              </ItemLink>
            </EllipsisContainer>

            <Spring />

            {props.task.successful && (
              <Tooltip content="Marked as successful">
                <Heart20Green />
              </Tooltip>
            )}

            {mostRecentPr && mostRecentPr.state != "closed" && (
              <GitHubPrBadge pr={mostRecentPr} />
            )}
          </FirstLine>
          <SecondLine>
            {profile && <Avatar profile={profile} size={20} />}

            <PrincipalsEllipsis>
              <span data-testid="task-card-author">
                <Principal principal={props.task.createdBy} stopPropagation />
              </span>

              {props.task.collaborators.length > 0 && (
                <>
                  <CollaboratorsDivider isLast={props.task.collaborators.length == 1} />
                  {props.task.collaborators.map((collaborator, index) => {
                    return (
                      <Fragment key={collaborator.id}>
                        {index > 0 && <CollaboratorsDivider isLast={index == props.task.collaborators.length - 1} />}
                        <Principal
                          principal={collaborator}
                          shortName={props.task.collaborators.length > 1}
                          stopPropagation
                        />
                      </Fragment>
                    )
                  })}
                </>
              )}
            </PrincipalsEllipsis>
            <Spring />

            <CreatedAt visible={!hovered}>{formatTime(props.task.createdAt)}</CreatedAt>

            <HoverActions visible={hovered}>
              <Tooltip content="Copy link">
                <Button tabIndex={hovered ? undefined : -1} kind="icon" onClick={(e) => {
                  e.stopPropagation()
                  void copyToClipboard(window.location.host + router.buildLocation(task).href)
                }} data-testid="task-card-copy-link">
                  <LinkLight />
                </Button>
              </Tooltip>
              <ConfirmDialog
                open={confirmDialogOpen}
                onOpenChange={setConfirmDialogOpen}
                trigger={
                  <Tooltip content="Delete">
                    <Button tabIndex={hovered ? undefined : -1} kind="icon" onClick={(e) => {
                      e.stopPropagation()
                      setConfirmDialogOpen(true)
                    }} data-testid="task-card-delete-button"><DeleteLight /></Button>
                  </Tooltip>
                }
                description="Are you sure you want to delete this idea?"
                confirmText="Delete"
                onConfirm={() => {
                  props.deleteItem(props.task.id)
                  setConfirmDialogOpen(false)
                }}
              />
            </HoverActions>
          </SecondLine>
        </Content>
      </Horizontal>
    </CardComponent>
  )
}

function getProfileFromPrincipal(principal: CPrincipal) {
  if (isUserPrincipal(principal)) {
    return principal.details.user
  }
  return null
}

const LimitedWidth = css`
  width: 80%;
  min-width: 560px;
  max-width: var(--limited-centered-width);

  @media (width <= 700px) {
    width: 100%;
    min-width: auto;
  }
`

const ProjectNameWrapper = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`

const ProjectNameContainer = styled(Horizontal)`
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const ProjectName = styled(Heading2)`
  color: var(--secondary-text-color);
  font-weight: 600;
  text-align: center;
`

const TaskListContainer = styled(HomeStyles.List)`
  view-transition-name: project-tasks;

  gap: 16px;
`

const ListTypeSwitchContainer = styled(Horizontal)`
  align-items: center;
  gap: 12px;

  padding: 0 0 0 8px;
  white-space: nowrap;

  view-transition-name: list-type-switch;
`

const SuccessfulToggle = styled(Link)`
  display: flex;
  align-items: center;

  view-transition-name: successful-toggle;
`

const SuccessfulActive = css`
  background: var(--text-color-5);
`

const NewTaskButton = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  height: 95px;
  margin-top: 16px;
  padding-right: 28px; /* Visually center the text (the icon is 20px, the gap is 8px) */
  color: var(--text-color-60);
  background: transparent;

  border: 1px solid var(--text-color-10);
  border-radius: var(--border-radius-16);

  font-weight: 500;

  transition: color 0.3s ease-in-out, border-color 0.3s ease-in-out, background 0.3s ease-in-out;

  &:hover {
    color: var(--main-color);
    border-color: var(--main-color-20);
    background: radial-gradient(ellipse at 50% 100%, var(--main-color-15) 0%, var(--main-color-5) 29%, transparent 100%);
  }
`

const Content = styled(Vertical)`
  overflow: hidden;
`

const ItemLink = styled(Link)`
  font-size: 18px;
  font-weight: 500;
  color: var(--text-color);

  &:hover {
    color: var(--text-color);
  }

  text-wrap: pretty;

  ${ellipsis};
`

const FirstLine = styled(Horizontal)`
  align-items: center;
  gap: 8px;

  width: 100%;

  line-height: 24px;
`

const SecondLine = styled(Horizontal)`
  position: relative;

  align-items: center;
  gap: 8px;

  width: 100%;
  white-space: nowrap;

  color: var(--secondary-text-color);

  & > ${ProfileLink}:hover {
    color: var(--text-color);
  }

  ${textStyles.smallFontSize};
`

const PrincipalsEllipsis = styled(Ellipsis)`
  max-width: calc(100% - 128px);
`

const CreatedAt = styled(Ellipsis)<{ visible: boolean }>`
  position: absolute;
  right: 0;

  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? "0" : "-8px"});
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;

  line-height: 24px;
`

const HoverActions = styled(Horizontal)<{ visible: boolean }>`
  position: absolute;
  right: 0;

  align-items: center;
  gap: 16px;

  opacity: ${props => props.visible ? 1 : 0};
  transform: translateX(${props => props.visible ? "0" : "8px"});
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
`

const FadeOutGradient = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;

  transform: translateX(-50%);

  width: calc(100% - (${PAGE_HORIZONTAL_PADDING} * 2));

  @media ${mobileBreakpoint} {
    width: calc(100% - (${PAGE_HORIZONTAL_PADDING_MOBILE} * 2));
  }

  height: 80px;

  background: linear-gradient(180deg, var(--background-color-0), var(--background-color) 100%);
  pointer-events: none;
`

const CollaboratorsDivider = styled.span<{ isLast: boolean }>`
  &::before {
    content: ${props => props.isLast ? "\"\\00a0&\\00a0\"" : "\",\\00a0\""};
  }

  color: var(--secondary-text-color);
`

const ErrorColoredButton = styled(Button)`
  color: var(--error-color-60);

  &:hover:not(:disabled), &[data-force-active="true"] {
    color: var(--error-color);
  }
`
